import React, { useEffect } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CircularProgress from '@mui/material/CircularProgress';
import MarkdownRenderer from './MarkdownRenderer';
import { Message } from './types';

interface ChatContentProps {
  chatRef: React.RefObject<HTMLDivElement>;
  chatHistory: Message[];
  isLoading: boolean;
  isStreaming?: boolean;
  input: string;
  setInput: (value: string) => void;
  handleSend: () => void;
}

export default function ChatContent({
  chatRef,
  chatHistory,
  isLoading,
  isStreaming = false,
  input,
  setInput,
  handleSend,
}: ChatContentProps) {
  const lastMessage = chatHistory[chatHistory.length - 1];
  const assistantHasStarted =
    lastMessage?.role === 'assistant' && lastMessage.content.trim().length > 0;

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [chatHistory]);

  useEffect(() => {
    const chatInput = document.getElementById('chat-input');
    if (chatInput) {
      chatInput.focus();
    }
  }, [open]);

  useEffect(() => {
    // Focus input when streaming is done
    if (!isStreaming) {
      const chatInput = document.getElementById('chat-input');
      if (chatInput) {
        chatInput.focus();
      }
    }
  }, [isStreaming]);

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box ref={chatRef} sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
        <List>
          {chatHistory.length === 0 && !isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                textAlign: 'center',
                color: 'gray',
              }}
            >
              <Typography variant="h6">Welcome! Start a conversation.</Typography>
            </Box>
          ) : (
            chatHistory.map((msg, index) => (
              <ListItem
                key={index}
                alignItems="flex-start"
                sx={{
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary' }}>
                  {msg.role === 'user' ? 'You' : 'Assistant'}
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    pb: msg.role === 'assistant' ? 2 : 0,
                    fontSize: '0.875rem',
                    borderRadius: 2,
                    backgroundColor: msg.role === 'user' ? '#d7f0eb' : '#f5f5f5',
                    ...(msg.role === 'assistant' && { '& > :last-child': { marginBottom: 0 } }),
                  }}
                >
                  <MarkdownRenderer content={msg.content} />
                </Box>
              </ListItem>
            ))
          )}
          {isLoading && !assistantHasStarted && (
            <ListItem>
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <ListItemText primary="Assistant is thinking..." />
              </Box>
            </ListItem>
          )}
        </List>
      </Box>
      <Stack spacing={2} direction="row" alignItems="center">
        <TextField
          id="chat-input"
          label="Your message"
          fullWidth
          multiline
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={isLoading}
          rows={2}
        />

        <IconButton
          size="large"
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          sx={{
            background: '#62AF99',
            color: 'white',
            width: 40,
            height: 40,
            '&:hover': {
              background: '#519a86',
            },
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}
